import type { PublicFixture, Sport } from "../../services/api/schemas";

/**
 * The landing page's ordering and windowing rules — kept as PURE FUNCTIONS,
 * away from Home.tsx and the "see more" page it feeds, because both carry
 * every edge case this file exists to get right: a sport with a game in
 * progress, a sport with nothing upcoming, and the "never fewer than 10
 * while 10 exist" floor. See Home.tsx and AllFixtures.tsx for how these
 * three are actually used.
 *
 * kev-docs/kev-landing-and-seo-plan.md (Ned, 2026-09-07 — reiterated the
 * same day, verbally): group by sport, order the sports by whichever starts
 * soonest, show the next 10 fixtures (or everything inside 48 hours if
 * that's more), and never show an empty state while any sport has a
 * fixture. "The site should always look populated."
 *
 * The count and the window are PER SPORT (Ned, 2026-09-08). Read globally
 * they defeated the grouping directly above them: 52 football fixtures meant
 * the next ten were all football, so every other sport was windowed off a
 * page whose whole premise is that it is grouped by sport.
 */

/**
 * How long after kickoff a fixture still counts as IN PROGRESS rather than
 * finished, per sport. `publicFixtureSchema` carries no status field (see its
 * own note in services/api/schemas.ts), so this is the only way this file can
 * tell "still going" from "long over" — a ceiling on how long a match of that
 * sport ever runs, not a live clock and not a score.
 *
 * ⚠️ ONLY FOOTBALL IS MEASURED. 120 minutes is a real margin for football — 90
 * plus stoppage plus a break comfortably fits under it. NBA/NFL/AFL have no
 * fixtures in the payload yet (today only football does — see Home.tsx's own
 * note), so their windows are placeholders sized the same conservative way
 * and should be revisited against real data once each sport's fixtures start
 * arriving here.
 */
export const IN_PROGRESS_WINDOW_MINUTES: Readonly<Record<Sport, number>> = {
  football: 120,
  nba: 150,
  nfl: 210,
  afl: 150,
};

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

function kickoffMs(fixture: PublicFixture): number {
  return new Date(fixture.kickoff).getTime();
}

/** Kicked off longer ago than that sport's in-progress window allows — over,
    as far as this file can tell without a status field. */
function isFinished(fixture: PublicFixture, now: Date): boolean {
  const elapsedMinutes = (now.getTime() - kickoffMs(fixture)) / MINUTE_MS;
  return elapsedMinutes > IN_PROGRESS_WINDOW_MINUTES[fixture.sport];
}

/**
 * Kicked off, and not yet past its sport's in-progress window — the plain
 * "Kicked off" label `Home.tsx` renders, never a score and never "LIVE".
 */
export function isInProgress(fixture: PublicFixture, now: Date): boolean {
  return kickoffMs(fixture) <= now.getTime() && !isFinished(fixture, now);
}

/** Upcoming, or in progress — the set this whole file ever shows. A finished
    fixture is dropped outright, not merely ranked last. */
function isEligible(fixture: PublicFixture, now: Date): boolean {
  return !isFinished(fixture, now);
}

/**
 * Tiebreaker ONLY, for two sports whose earliest eligible fixture kicks off
 * at the exact same instant — the old fixed `SPORT_ORDER` Home.tsx carried
 * before sport order became dynamic, demoted from primary key to fallback so
 * the result stays deterministic rather than depending on array/object
 * iteration order.
 */
const SPORT_TIEBREAK_ORDER: readonly Sport[] = ["football", "nba", "nfl", "afl"];

/**
 * Sport order for the landing page: the sport whose earliest ELIGIBLE
 * fixture kicks off soonest goes first.
 *
 * A fixture already in progress has a kickoff in the past, which sorts
 * before any fixture that hasn't started yet — so a sport with a live game
 * outranks one that is merely next with no special case required here; it
 * falls out of comparing timestamps.
 *
 * A sport with no eligible fixture anywhere in `fixtures` is simply absent
 * from the result — never an empty heading. Pass this function the SAME
 * windowed list you are about to render (`selectLanding`/`selectFullList`'s
 * result), not the unfiltered feed, so "absent" means "nothing in the list
 * about to be shown" and not "nothing in the whole feed".
 */
export function orderSportsByNextStart(
  fixtures: readonly PublicFixture[],
  now: Date,
): Sport[] {
  const earliest = new Map<Sport, number>();
  for (const f of fixtures) {
    if (!isEligible(f, now)) continue;
    const t = kickoffMs(f);
    const current = earliest.get(f.sport);
    if (current === undefined || t < current) earliest.set(f.sport, t);
  }

  return [...earliest.keys()].sort((a, b) => {
    const diff = earliest.get(a)! - earliest.get(b)!;
    if (diff !== 0) return diff;
    return SPORT_TIEBREAK_ORDER.indexOf(a) - SPORT_TIEBREAK_ORDER.indexOf(b);
  });
}

/**
 * The shared rule behind `selectLanding` and `selectFullList`, applied
 * SEPARATELY TO EACH SPORT: for one sport, show every eligible fixture inside
 * `windowHours`, or that sport's next `minCount` if the window holds fewer.
 *
 * ── WHY PER SPORT (Ned, 2026-09-08) ───────────────────────────────────────
 * This used to sort every sport together and take the first `minCount`. On a
 * European Saturday that is 52 football fixtures, so the next ten are all
 * football and the other sports vanish from the landing page — not because
 * they have nothing on, but because somebody else's slate is denser. A reader
 * who came for the NFL saw no NFL. Per-sport windowing means each sport gets
 * its own floor and its own 48 hours, and a quiet sport can never be squeezed
 * out by a busy one.
 *
 * The ordering ACROSS sports is not decided here — `orderSportsByNextStart`
 * owns that, so which sport leads the page stays a separate question from how
 * much of each sport is shown.
 *
 * An in-progress fixture's kickoff is always <= `now`, so it always falls
 * inside any forward-looking window and always sorts into the earliest
 * positions — it cannot be windowed or counted out by either rule.
 */
function selectWindow(
  fixtures: readonly PublicFixture[],
  now: Date,
  minCount: number,
  windowHours: number,
): PublicFixture[] {
  const eligible = fixtures
    .filter((f) => isEligible(f, now))
    .sort((a, b) => kickoffMs(a) - kickoffMs(b));

  const windowEndMs = now.getTime() + windowHours * HOUR_MS;

  const bySport = new Map<Sport, PublicFixture[]>();
  for (const f of eligible) {
    const list = bySport.get(f.sport);
    if (list) list.push(f);
    else bySport.set(f.sport, [f]);
  }

  const kept = new Set<PublicFixture>();
  for (const list of bySport.values()) {
    const withinWindow = list.filter((f) => kickoffMs(f) <= windowEndMs);
    const take = withinWindow.length > minCount ? withinWindow : list.slice(0, minCount);
    for (const f of take) kept.add(f);
  }

  // Re-filtered from `eligible` rather than concatenated per sport, so the
  // returned list keeps ONE global kickoff order. Grouping for display is
  // `FixtureListSection`'s job, and handing it a list already clumped by sport
  // would quietly make that grouping untestable.
  return eligible.filter((f) => kept.has(f));
}

/**
 * The landing page's fixture set: PER SPORT, everything inside the next 48
 * hours, or that sport's next 10 when the window holds fewer. Never fewer than
 * 10 for a sport while 10 of its fixtures exist.
 */
export function selectLanding(fixtures: readonly PublicFixture[], now: Date): PublicFixture[] {
  return selectWindow(fixtures, now, 10, 48);
}

/**
 * The "see more" page's fixture set (`/fixtures`): the same per-sport rule, a
 * bigger floor — about 20 a sport, or everything inside 48 hours if that is
 * more.
 */
export function selectFullList(fixtures: readonly PublicFixture[], now: Date): PublicFixture[] {
  return selectWindow(fixtures, now, 20, 48);
}
